import {
  CHANNEL_INTERFACE_TYPE,
  CHANNEL_INTERFACE_TEXT,
  ADVERTISER_SOURCE_TYPE,
  ADVERTISER_SOURCE_TEXT,
} from '@/enums';
import { clone } from '@/utils';

/**
 * 将数据转化为 [{label: '', value: ''}] 格式的数组
 * @param {Object} config - 配置对象
 * @param {Object} config.values - 包含数据类型的值的对象
 * @param {Object} config.labels - 与config.values 对象的键相对应，包含数据的标签
 * @param {Object} [config.options={}] - 配置选项
 * @param {string} [config.options.label_key='label'] - 指定转化后标签的键名
 * @param {string} [config.options.value_key='value'] - 指定转化后值的键名
 * @returns {Array} - 转化后的数组
 * @example
 * transformEnumToOptions({ labels: { TEST: '测试' }, values: { TEST: 'test' }})
 * 输出：[{"label": "测试","value": "test" }]
 *
 * transformEnumToOptions({ labels: { TEST: '测试' }, values: { TEST: 'test' }, options: { label_key: 'name', value_key: 'id' } });
 * 输出：[{"name": "测试","id": "test"}]
 */
export function transformEnumToOptions(config) {
  const isObject = (val) => Object.prototype.toString.call(val) === '[object Object]';

  if (!isObject(config)) {
    throw new Error('Both config must be objects.');
  }

  const { labels, values, options = {}} = config;

  // 验证输入是否为对象
  if (Object.values(config).some(c => !isObject(c))) {
    throw new Error(`${Object.keys(config).join('、')} must be objects.`);
  }

  const { label_key = 'label', value_key = 'value' } = options;

  const result = [];

  for (const key in values) {
    if (values.hasOwnProperty(key) && labels.hasOwnProperty(key)) {
      const value = values[key];
      const label = labels[key];
      result.push({ [label_key]: label, [value_key]: value });
    }
  }

  // 如果结果为空，并且 values 和 labels 对象都不为空，则可能是没有公共键
  if (result.length === 0 && Object.keys(values).length > 0 && Object.keys(labels).length > 0) {
    console.warn('No common keys found between values and labels objects.');
  }

  return result;
}

/**
 * 枚举转换为对象
 * @param {*} enum_keys 枚举键
 * @param {*} enum_values 枚举值
 * @returns {Object}
 * transformEnumToObject({ TEST: 'test' }, { TEST: '测试' });
 * 输出：{ test: '测试' }
 */
export function transformEnumToObject(enum_keys, enum_values) {
  const result = {};

  Object.keys(enum_keys).forEach(key => {
    Object.assign(result, { [enum_keys[key]]: enum_values[key] });
  });

  return result;
}

/**
 * 对象转为options数据结构
 * @param {Object} data 数据对象
 * @returns {Array<Object>}
 * @example
 * transformObjectToOptions({ active: "激活"})
 * 输出：[{"label": "激活", "value": "active" }]
 *
 * transformObjectToOptions({ active: "激活"}, { label_key = 'name', value_key = 'id' })
 * 输出：[{"name": "激活", "id": "active" }]
 */
export function transformObjectToOptions(data, options = {}) {
  const { label_key = 'label', value_key = 'value' } = options;
  return Object.keys(data).map(key => {
    return {
      [label_key]: data[key],
      [value_key]: key,
    };
  });
}

/**
 * 剔除不支持的媒体渠道数据
 * 目前剔除快手
 * @param {Object} raw_data 原始数据
 * @returns {Object}
 */
function omitChannels(raw_data) {
  const data = clone(raw_data);
  const exclude_channels = [data.KUAISHOU];
  Object.keys(data).forEach(key => {
    if (exclude_channels.includes(data[key])) {
      delete data[key];
    }
  });
  return data;
}

/**
 * 获取支持的媒体渠道
 * @returns {Object}
 */
export function getSupportChannelTypeMap() {
  return omitChannels(CHANNEL_INTERFACE_TYPE);
}

/**
 * 获取支持的媒体渠道类型
 * @returns {Array<string>}
 */
export function getSupportChannelTypes() {
  return Object.values(getSupportChannelTypeMap());
}

/**
 * 获取支持的媒体渠道名称
 * @returns {Object}
 */
export function getSupportChannelTextMap() {
  return omitChannels(CHANNEL_INTERFACE_TEXT);
}

/**
 * 获取支持的渠道选项
 * @returns {Array<Object>}
 */
export function getSupportChannelOptions() {
  return transformEnumToOptions({
    labels: getSupportChannelTextMap(),
    values: getSupportChannelTypeMap(),
  });
}

/**
 * 获取可编辑渠道号的渠道
 * @returns {Array<string>}
 */
export function getChannelsWithEditableCode() {
  const channels = getSupportChannelTypes();
  const exclude_channels = [
    CHANNEL_INTERFACE_TYPE.GDT,
    CHANNEL_INTERFACE_TYPE.JRTT,
    CHANNEL_INTERFACE_TYPE.BILIBILI,
    CHANNEL_INTERFACE_TYPE.BAIDU,
  ];
  return channels.filter(type => !exclude_channels.includes(type));
}

/**
 * 获取可编辑落地页的渠道
 * @returns {Array<string>}
 */
export function getChannelsWithEditableSite() {
  const channels = getSupportChannelTypes();
  const exclude_channels = [CHANNEL_INTERFACE_TYPE.XIAOMI];
  return channels.filter(type => !exclude_channels.includes(type));
}

/**
 * 获取支持的资源类型
 * @returns {Object[]}
 */
export function getAdvertiserSourceTypeOptions() {
  return transformEnumToOptions({
    labels: ADVERTISER_SOURCE_TEXT,
    values: ADVERTISER_SOURCE_TYPE,
  });
}

/**
 * 获取支持批量操作记录的渠道选项
 * @returns {Object[]}
 */
export function getChannelOptionsWithBatchCreateLog() {
  const options = getSupportChannelOptions();
  const support_channels = [CHANNEL_INTERFACE_TYPE.JRTT];
  return options.filter(({ value }) => support_channels.includes(value));
}

/**
 * 获取支持投放管理的渠道选项
 * @returns {Object[]}
 */
export function getChannelOptionsWithPutInManage() {
  const options = getSupportChannelOptions();
  const support_channels = [CHANNEL_INTERFACE_TYPE.JRTT];
  return options.filter(({ value }) => support_channels.includes(value));
}
